/**
 * quick-add-parser.ts — Pure NL parser for inbox quick-add.
 * Vietnamese-first: relative dates, times, priority tokens, #tags.
 * Conservative: unrecognized text stays in title; no silent create.
 * TIMEZONE: uses date.ts helpers (todayIso / addDaysIso) — never toISOString().slice.
 */
import { todayIso, addDaysIso } from "./date";

// ── Output type ───────────────────────────────────────────────────────────────

export interface QuickAddResult {
  title: string;
  dueDate?: string;  // ISO YYYY-MM-DD local
  time?: string;     // "HH:MM" 24h
  priority?: "low" | "medium" | "high";
  tags: string[];
}

// ── Day-of-week mapping (VN + EN) ────────────────────────────────────────────

/** Returns ISO local date for next occurrence of target DOW (0=Sun..6=Sat), >= today. */
function nextWeekday(targetDow: number, now: Date): string {
  const currentDow = now.getDay();
  // days ahead: 0 if same day, else wrap-around
  let daysAhead = (targetDow - currentDow + 7) % 7;
  if (daysAhead === 0) daysAhead = 7; // next occurrence, not today
  return addDaysIso(daysAhead, now);
}

/** Parse day-of-week token. Returns ISO date or null. */
function parseDayOfWeek(token: string, now: Date): string | null {
  // thứ 2..7 (Mon=2..Sat=7), thứ hai..bảy; also "cn" / chủ nhật for Sunday
  const vnMap: Record<string, number> = {
    "2": 1, "hai": 1,
    "3": 2, "ba": 2,
    "4": 3, "tư": 3, "tu": 3,
    "5": 4, "năm": 4, "nam": 4,
    "6": 5, "sáu": 5, "sau": 5,
    "7": 6, "bảy": 6, "bay": 6,
    "cn": 0, "chủ nhật": 0, "chu nhat": 0,
  };
  const lower = token.toLowerCase();
  const thuMatch = lower.match(/^thứ\s+(.+)$/) ?? lower.match(/^thu\s+(.+)$/);
  if (thuMatch) {
    const key = thuMatch[1].trim();
    const dow = vnMap[key];
    if (dow !== undefined) return nextWeekday(dow, now);
  }
  return null;
}

// ── Time parser ───────────────────────────────────────────────────────────────

/** Parse time token. Returns "HH:MM" or null. */
function parseTime(token: string): string | null {
  // 3pm, 3am, 15h, 09:00, 9h30, 3:30pm
  const lower = token.toLowerCase();

  // HH:MM or H:MM optionally followed by am/pm
  const colonMatch = lower.match(/^(\d{1,2}):(\d{2})(am|pm)?$/);
  if (colonMatch) {
    let h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    if (m < 0 || m > 59 || h < 0 || h > 23) return null;
    if (colonMatch[3] === "pm" && h < 12) h += 12;
    if (colonMatch[3] === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  // Nh30 or Nh  (Vietnamese: 3h, 9h30, 15h)
  const hMatch = lower.match(/^(\d{1,2})h(\d{2})?$/);
  if (hMatch) {
    const h = parseInt(hMatch[1], 10);
    const m = hMatch[2] ? parseInt(hMatch[2], 10) : 0;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  // Npm / Nam (3pm, 11am)
  const ampmMatch = lower.match(/^(\d{1,2})(am|pm)$/);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    if (h < 1 || h > 12) return null;
    if (ampmMatch[2] === "pm" && h < 12) h += 12;
    if (ampmMatch[2] === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:00`;
  }

  return null;
}

// ── Priority parser ───────────────────────────────────────────────────────────

function parsePriority(token: string): "low" | "medium" | "high" | null {
  const lower = token.toLowerCase();
  if (lower === "!cao" || lower === "!high") return "high";
  if (lower === "!tb" || lower === "!med" || lower === "!medium" || lower === "!trung") return "medium";
  if (lower === "!thấp" || lower === "!thap" || lower === "!low") return "low";
  return null;
}

// ── Main parser ───────────────────────────────────────────────────────────────

/**
 * Parse NL quick-add text into structured fields.
 * @param text Raw user input
 * @param now  Reference date (defaults to current local date). Pass a fixed Date for tests.
 */
export function parseQuickAdd(text: string, now: Date = new Date()): QuickAddResult {
  const todayStr = todayIso(now);
  const tokens = text.trim().split(/\s+/);
  const usedIndices = new Set<number>();

  let dueDate: string | undefined;
  let time: string | undefined;
  let priority: "low" | "medium" | "high" | undefined;
  const tags: string[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    const lower = tok.toLowerCase();

    // ── relative date keywords ────────────────────────────────────────────────
    // Two-word "hôm nay" must be checked BEFORE single "hôm" fallback
    if (lower === "hôm" && i + 1 < tokens.length && tokens[i + 1].toLowerCase() === "nay") {
      dueDate ??= todayStr;
      usedIndices.add(i);
      usedIndices.add(i + 1);
      i++;
      continue;
    }
    if (lower === "hôm_nay" || lower === "hom_nay") {
      dueDate ??= todayStr;
      usedIndices.add(i);
      continue;
    }
    if (lower === "mai" || lower === "ngày_mai") {
      dueDate ??= addDaysIso(1, now);
      usedIndices.add(i);
      continue;
    }
    if (lower === "ngày" && i + 1 < tokens.length && tokens[i + 1].toLowerCase() === "mai") {
      dueDate ??= addDaysIso(1, now);
      usedIndices.add(i);
      usedIndices.add(i + 1);
      i++;
      continue;
    }
    if (lower === "mốt" || lower === "mot") {
      dueDate ??= addDaysIso(2, now);
      usedIndices.add(i);
      continue;
    }

    // ── thứ N / thứ hai..bảy (1-word: "thứ2" or "t2", or 2-words "thứ hai") ──
    const thuInline = lower.match(/^(?:thứ|thu)([2-7]|hai|ba|tư|tu|năm|nam|sáu|sau|bảy|bay)$/);
    if (thuInline) {
      const key = thuInline[1];
      const parsed = parseDayOfWeek(`thứ ${key}`, now);
      if (parsed) {
        dueDate ??= parsed;
        usedIndices.add(i);
        continue;
      }
    }
    // 2-word: "thứ" + "hai"
    if ((lower === "thứ" || lower === "thu") && i + 1 < tokens.length) {
      const combined = `thứ ${tokens[i + 1].toLowerCase()}`;
      const parsed = parseDayOfWeek(combined, now);
      if (parsed) {
        dueDate ??= parsed;
        usedIndices.add(i);
        usedIndices.add(i + 1);
        i++;
        continue;
      }
    }

    // ── time ─────────────────────────────────────────────────────────────────
    const parsedTime = parseTime(tok);
    if (parsedTime) {
      time ??= parsedTime;
      usedIndices.add(i);
      continue;
    }

    // ── priority ─────────────────────────────────────────────────────────────
    const parsedPri = parsePriority(tok);
    if (parsedPri) {
      priority ??= parsedPri;
      usedIndices.add(i);
      continue;
    }

    // ── tags ──────────────────────────────────────────────────────────────────
    if (tok.startsWith("#") && tok.length > 1) {
      tags.push(tok.slice(1));
      usedIndices.add(i);
      continue;
    }
  }

  // Reconstruct title from unused tokens
  const title = tokens.filter((_, i) => !usedIndices.has(i)).join(" ").trim() || text.trim();

  return { title, dueDate, time, priority, tags };
}
